import { type SubmitEvent, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { marketplacePartnerRegisterPageBuildRequestBodyFromModel } from '../api/swr/marketplacePartnerRegisterPageBuildRequestBodyFromModel'
import { useMarketplacePartnerRegisterPageFormApiPostMutationV3WithBody } from '../api/swr/useMarketplacePartnerRegisterPageFormApiPostMutationV3WithBody'
import { useMarketplacePartnerRegisterPageContext } from '../components/marketplacePartnerRegisterPageContext'
import { marketplacePartnerRegisterPageFormScrollToFirstFormError } from '../marketplacePartnerRegisterPageFormScrollToFirstFormError'

import {
  marketplacePartnerRegisterPageFormV3DefaultValues,
  type MarketplacePartnerRegisterPageFormV3Values,
} from './marketplacePartnerRegisterPageFormV3RHFModel'

const scrollToFirstFormError = (formElement: HTMLFormElement | null): void => {
  requestAnimationFrame(() => {
    if (formElement) {
      marketplacePartnerRegisterPageFormScrollToFirstFormError(formElement)
    }
  })
}

const hasRequiredArrayValues = (
  values: MarketplacePartnerRegisterPageFormV3Values,
): boolean => {
  return values.categories.length > 0 && values.targetCountries.length > 0
}

const setMissingArrayErrors = ({
  form,
  message,
  values,
}: {
  form: ReturnType<typeof useForm<MarketplacePartnerRegisterPageFormV3Values>>
  message: string
  values: MarketplacePartnerRegisterPageFormV3Values
}): void => {
  if (values.categories.length === 0) {
    form.setError('categories', { message })
  }

  if (values.targetCountries.length === 0) {
    form.setError('targetCountries', { message })
  }
}

export const useMarketplacePartnerRegisterPageSendEmailV3 = (): {
  errors: unknown
  form: ReturnType<typeof useForm<MarketplacePartnerRegisterPageFormV3Values>>
  handleSendEmail: (event: SubmitEvent) => Promise<void>
  isMutating: boolean
  setCaptchaSolution: (fcSolution: string) => void
} => {
  const intl = useIntl()
  const { formRef, resetForm, setIsFormValid } =
    useMarketplacePartnerRegisterPageContext()
  const missingError = intl.formatMessage({ id: 'form.error.input.missing' })

  const form = useForm<MarketplacePartnerRegisterPageFormV3Values>({
    defaultValues: marketplacePartnerRegisterPageFormV3DefaultValues,
    mode: 'onBlur',
  })

  const marketplacePartnerRegisterPageApiPostMutationV3WithBody =
    useMarketplacePartnerRegisterPageFormApiPostMutationV3WithBody()

  const setCaptchaSolution = useCallback(
    (fcSolution: string): void => {
      form.setValue('fcSolution', fcSolution)
    },
    [form],
  )

  const onInvalid = useCallback((): void => {
    setIsFormValid(false)
    scrollToFirstFormError(formRef.current)
  }, [formRef, setIsFormValid])

  const onValid = useCallback(
    async (
      values: MarketplacePartnerRegisterPageFormV3Values,
    ): Promise<void> => {
      if (!hasRequiredArrayValues(values)) {
        setMissingArrayErrors({ form, message: missingError, values })
        setIsFormValid(false)
        scrollToFirstFormError(formRef.current)

        return
      }

      setIsFormValid(true)

      if (marketplacePartnerRegisterPageApiPostMutationV3WithBody.isMutating) {
        return
      }

      const requestBody =
        marketplacePartnerRegisterPageBuildRequestBodyFromModel(values)

      try {
        await marketplacePartnerRegisterPageApiPostMutationV3WithBody.trigger(
          requestBody,
        )
        resetForm()
        form.reset(marketplacePartnerRegisterPageFormV3DefaultValues)
      } catch {
        // void -- error will be handled in MarketplacePartnerRegisterPageFormMessages component
      }
    },
    [
      form,
      formRef,
      marketplacePartnerRegisterPageApiPostMutationV3WithBody,
      missingError,
      resetForm,
      setIsFormValid,
    ],
  )

  const handleSendEmail = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault()

    await form.handleSubmit(onValid, onInvalid)(event)
  }

  return {
    errors: marketplacePartnerRegisterPageApiPostMutationV3WithBody.error,
    form,
    handleSendEmail,
    isMutating:
      marketplacePartnerRegisterPageApiPostMutationV3WithBody.isMutating,
    setCaptchaSolution,
  }
}
